export const URL_BASE = (window as any).APP_CONFIG?.API_BASE ?? "/camera";

export interface FetchProgress {
    /** current bytes (include this data chunk) */
    current: number;
    /** total bytes, -1 means unknown */
    total: number;
    /** data chunk */
    data: Uint8Array<ArrayBufferLike>;
}

export class APIRequestError extends Error {
    statusCode: number;
    errorCode: number;
    constructor(statusCode: number, message: string) {
        super(message);
        this.name = "Request Error";
        this.statusCode = statusCode;
        this.errorCode = -1;
        try {
            // parse error message
            const msg = JSON.parse(message);
            this.errorCode = msg?.subcode ?? -1;
            this.message = msg?.msg ?? message;
        } catch {
            // ignore error
        }
    }
}

const getRequestLanguage = () => {
    let lang = globalThis?.document?.documentElement?.getAttribute("lang") ?? "";
    lang = lang.trim().toLowerCase();
    if (!lang) return "";
    if (lang === "zh") return "zh-Hans";
    return lang;
};

const parseRequestError = (err: unknown): APIRequestError => {
    if (err instanceof APIRequestError) {
        return err;
    } else if (err instanceof Error) {
        return new APIRequestError(-1, err.message ?? "Unknown");
    }
    return new APIRequestError(-1, "Unknown");
};

export const requestGet = async <T>(path: string, queries: Record<string, string | number | boolean | undefined> = {}) => {
    const url = new URL(URL_BASE + path, globalThis.location.toString());
    for (const key in queries) {
        if (Object.prototype.hasOwnProperty.call(queries, key)) {
            const element = queries[key];
            if (element !== undefined)
                url.searchParams.set(key, element.toString());
        }
    }
    try {
        const lang = getRequestLanguage();
        const resp = await fetch(url, {
            method: "GET",
            headers: lang ? { "Accept-Language": lang } : {},
            credentials: "same-origin",
        });
        if (resp.status != 200) {
            throw new APIRequestError(resp.status, await resp.text());
        }
        return await resp.json() as T;
    } catch (err: unknown) {
        throw parseRequestError(err);
    }
};

const getCookie = (name: string) => {
    let cookieValue = "";
    if (document.cookie && document.cookie !== "") {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + "=")) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
};
const getCSRFToken = () => getCookie("csrftoken");

export const requestWithParams = async <T>(method: string, path: string, params: Record<string, unknown> = {}) => {
    const url = new URL(URL_BASE + path, globalThis.location.toString());
    const data = JSON.stringify(params);
    try {
        const lang = getRequestLanguage();
        const resp = await fetch(url, {
            method: method,
            headers: {
                "X-CSRFToken": getCSRFToken(),
                ...(lang ? { "Accept-Language": lang } : {}),
            },
            credentials: "same-origin",
            body: data,
        });
        if (resp.status != 200) {
            throw new APIRequestError(resp.status, await resp.text());
        }
        return await resp.json() as T;
    } catch (err: unknown) {
        throw parseRequestError(err);
    }
};

export const requestPost = async <T>(path: string, params: Record<string, unknown> = {}) => {
    return await requestWithParams<T>("POST", path, params);
};

export const requestDelete = async <T>(path: string, params: Record<string, unknown> = {}) => {
    return await requestWithParams<T>("DELETE", path, params);
};

export const downloadSimpleFile = async function (url: RequestInfo | URL, init?: RequestInit) {
    try {
        const resp = await fetch(url, init);
        if (resp.status !== 200) {
            throw new APIRequestError(resp.status, resp.statusText);
        }
        try {
            return await resp.bytes();
        } catch (err) {
            throw new APIRequestError(200, (err as Error)?.message ?? "Network Error");
        }
    } catch (err) {
        throw parseRequestError(err);
    }
};

export const downloadWithStream = async function* (url: RequestInfo | URL, init?: RequestInit) {
    try {
        const resp = await fetch(url, init);
        if (resp.status !== 200) {
            throw new APIRequestError(resp.status, resp.statusText);
        }
        const total = Number.parseInt(resp.headers.get("Content-Length") ?? "-1");
        let current = 0;
        yield {
            current,
            total,
            data: new Uint8Array(0),
        } as FetchProgress;
        if (!resp.body) throw new APIRequestError(200, "Empty response body");
        try {
            const reader = resp.body.getReader();
            do {
                const { done, value } = await reader.read();
                if (done || (value === undefined)) break;
                current += value.byteLength;
                yield {
                    current,
                    total,
                    data: value,
                } as FetchProgress;
            } while (true);
            // read end
        } catch (err) {
            throw new APIRequestError(200, (err as Error)?.message ?? "Network Error");
        }
    } catch (err) {
        throw parseRequestError(err);
    }
};

export const getRequestUrl = (path: string) => {
    return new URL(URL_BASE + path, globalThis.location.toString()).toString();
};
